import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { NumberFormatValues, NumericFormat } from 'react-number-format'

import { FieldErrorText } from '@cash-web/shared-feature-form'

import { GiantInput } from '../GiantInput/GiantInput'

const GIANT_INPUT_PLACEHOLDER = '0%'

const Container = styled.div<{ shift: boolean }>`
  input {
    padding-left: ${props => (props.shift ? '0.4ch' : '0')};
  }

  input::placeholder {
    padding-left: 0.4ch;
  }
`

export const GiantPercentageInput = ({
  name,
  minimumLimit,
  maximumLimit,
  formattedValue,
  value,
  setFormattedValue,
  ariaLabel,
  isAllowed,
}: {
  name: string
  minimumLimit: number
  maximumLimit: number
  formattedValue: string
  value?: number
  setFormattedValue: (value: string) => void
  ariaLabel?: ({ formattedValue }: { formattedValue: string }) => string
  isAllowed?: (values: NumberFormatValues) => boolean
}) => {
  const { t } = useTranslation()
  const { setValue, register, formState } = useFormContext()
  const { errors, dirtyFields, isSubmitted } = formState

  const { onBlur, ...inputProps } = register(name, {
    required: {
      value: true,
      message: t('Amount is required.', { context: 'Error message: amount not specified' }),
    },
    ...(value !== undefined && { value }),
    validate: (value: string) => {
      const valueAsNumber = Number(value)
      if (Number.isNaN(valueAsNumber)) {
        return t('Amount must be a number.', {
          context: 'Error message: amount is not a number',
        })
      }
      if (valueAsNumber < minimumLimit) {
        return t('Amount must be at least {minLimit}%.', {
          context: 'Error message under input',
          minLimit: minimumLimit,
        })
      }
      if (valueAsNumber > maximumLimit) {
        return t('Amount must be less than or equal to {maxLimit}%.', {
          context: 'Error message under input',
          maxLimit: maximumLimit,
        })
      }
      return true
    },
  })

  const error = errors[name]
  const showError = error && (error?.type !== 'required' || isSubmitted)
  return (
    <Container shift={!!formattedValue}>
      <NumericFormat
        allowLeadingZeros={false}
        placeholder={GIANT_INPUT_PLACEHOLDER}
        customInput={GiantInput}
        value={formattedValue}
        suffix="%"
        decimalScale={0}
        thousandSeparator={true}
        onValueChange={target => {
          setFormattedValue(target.formattedValue)
          const value = target.value
          setValue(name, value)
        }}
        {...inputProps}
        onBlur={(e: React.FocusEvent) => {
          if (dirtyFields[name]) {
            onBlur(e)
          }
        }}
        isAllowed={isAllowed}
        autoComplete="off"
        autoFocus
        aria-label={
          ariaLabel
            ? ariaLabel({
                formattedValue: formattedValue || GIANT_INPUT_PLACEHOLDER,
              })
            : undefined
        }
        className={showError ? 'error' : ''}
      />

      <FieldErrorText name={name} />
    </Container>
  )
}
